
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Award, Book, Clock, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skill } from "./types";

interface SkillCardProps {
  skill: Skill;
  onStartAssessment: (skillName: string) => void;
}

export const SkillCard = ({ skill, onStartAssessment }: SkillCardProps) => {
  return (
    <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <div className="p-2 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50">
            <skill.icon className="w-5 h-5 text-purple-600" />
          </div>
          <Badge
            variant="outline"
            className={cn(
              "text-sm",
              skill.difficulty === "Beginner" && "bg-blue-50 text-blue-600 border-blue-200",
              skill.difficulty === "Intermediate" && "bg-yellow-50 text-yellow-600 border-yellow-200",
              skill.difficulty === "Advanced" && "bg-red-50 text-red-600 border-red-200"
            )}
          >
            {skill.difficulty}
          </Badge>
        </div>
        <CardTitle className="text-xl mb-1">{skill.name}</CardTitle>
        <CardDescription>{skill.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Book className="w-4 h-4" />
              {skill.questionsCount} Questions
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {skill.duration}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {skill.popularity}% Popularity
            </span>
            <span className="flex items-center gap-1">
              <Award className="w-4 h-4 text-emerald-600" />
              {skill.topicsCount} Topics
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Completion Rate</span>
              <span>{skill.completionRate}%</span>
            </div>
            <Progress value={skill.completionRate} className="h-2" />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Prerequisites:</p>
            <div className="flex flex-wrap gap-2">
              {skill.prerequisites.map((prereq, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {prereq}
                </Badge>
              ))}
            </div>
          </div>

          <Button
            className="w-full group-hover:bg-purple-600 bg-purple-500"
            onClick={() => onStartAssessment(skill.name)}
          >
            Start Assessment
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
